import React, { useCallback, useEffect, useState } from 'react';
import { whatsappBaileysAPI } from '../services/api';
import {
  readSendRateProfileFromStorage,
  writeSendRateProfileToStorage,
} from '../utils/whatsappBaileys';

/**
 * Modo Baileys vs wa.me, estado/QR y perfil anti-bloqueo.
 */
export function WhatsAppServicioPanel({
  useBaileys,
  onToggleUseBaileys,
  status,
  loading,
  onRefresh,
  sendRateProfile: sendRateProfileProp,
  onSendRateProfileChange,
  estimateCount,
}) {
  const [sendRateProfile, setSendRateProfile] = useState(
    () => sendRateProfileProp || readSendRateProfileFromStorage()
  );
  const [profilesMeta, setProfilesMeta] = useState([]);
  const [sessionHint, setSessionHint] = useState('');
  const [estimateHint, setEstimateHint] = useState('');

  const activeProfile = sendRateProfileProp ?? sendRateProfile;

  const handleProfileChange = (value) => {
    if (onSendRateProfileChange) {
      onSendRateProfileChange(value);
    } else {
      setSendRateProfile(value);
    }
    writeSendRateProfileToStorage(value);
  };

  const loadSendRateMeta = useCallback(async () => {
    if (!useBaileys) return;
    try {
      const [profRes, sessRes] = await Promise.all([
        whatsappBaileysAPI.getSendRateProfiles(),
        whatsappBaileysAPI.getSendRateSession(activeProfile),
      ]);
      if (profRes.profiles) setProfilesMeta(profRes.profiles);
      if (sessRes.totalSent != null) {
        setSessionHint(
          `Hoy: ${sessRes.totalSent} envío(s) (quedan ~${sessRes.remaining ?? '?'} con perfil ${activeProfile}).`
        );
      }
      if (estimateCount > 0) {
        const est = await whatsappBaileysAPI.getSendRateEstimate(estimateCount, activeProfile);
        if (est.estimateMinutes != null) {
          setEstimateHint(
            `Tiempo estimado (pausas anti-bloqueo): ~${est.estimateMinutes} min (perfil ${est.profile}).`
          );
        }
      } else {
        setEstimateHint('');
      }
    } catch {
      setSessionHint('');
    }
  }, [useBaileys, activeProfile, estimateCount]);

  useEffect(() => {
    loadSendRateMeta();
  }, [loadSendRateMeta]);

  const profileHint = profilesMeta.find((p) => p.id === activeProfile);

  useEffect(() => {
    if (!useBaileys || !status?.reachable || status?.connected || status?.qrBase64) return undefined;
    const id = setInterval(() => onRefresh(), 4000);
    return () => clearInterval(id);
  }, [useBaileys, status?.reachable, status?.connected, status?.qrBase64, onRefresh]);

  return (
    <div
      style={{
        marginBottom: 14,
        padding: 12,
        background: '#f0f7f2',
        borderRadius: 8,
        border: '1px solid #c8e6c9',
        fontSize: 13,
      }}
    >
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
        <input
          type="checkbox"
          checked={useBaileys}
          onChange={(e) => onToggleUseBaileys(e.target.checked)}
        />
        Enviar con servicio Baileys (desde el servidor)
      </label>
      {!useBaileys && (
        <p style={{ margin: '8px 0 0', color: '#555' }}>
          Si desactiva esta opción se abrirá <code>wa.me</code> en el navegador por cada destinatario.
        </p>
      )}
      {useBaileys && (
        <div style={{ marginTop: 10 }}>
          <p style={{ margin: '0 0 8px', color: '#5d4037', fontSize: 12 }}>
            WhatsApp puede limitar cuentas con envío masivo. Para campañas grandes (80+) use el perfil{' '}
            <strong>Seguro</strong>.
          </p>
          <div style={{ marginBottom: 8 }}>
            <label htmlFor="wa-send-rate-profile" style={{ marginRight: 8 }}>
              Perfil anti-bloqueo:
            </label>
            <select
              id="wa-send-rate-profile"
              value={activeProfile}
              onChange={(e) => handleProfileChange(e.target.value)}
              className="search-input"
            >
              <option value="fast">Rápido</option>
              <option value="normal">Normal</option>
              <option value="safe">Seguro</option>
            </select>
          </div>
          {profileHint && (
            <p style={{ margin: '0 0 6px', color: '#666', fontSize: 12 }}>{profileHint.description}</p>
          )}
          {sessionHint && (
            <p style={{ margin: '0 0 6px', color: '#666', fontSize: 12 }}>{sessionHint}</p>
          )}
          {estimateHint && (
            <p style={{ margin: '0 0 8px', color: '#1565c0', fontSize: 12 }}>{estimateHint}</p>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => onRefresh()}
            disabled={loading}
            style={{ marginBottom: 8 }}
          >
            {loading ? 'Consultando…' : 'Actualizar estado / QR'}
          </button>
          {status && (
            <div>
              {!status.reachable && (
                <p style={{ color: '#c62828', margin: '6px 0' }}>
                  {status.serviceError ||
                    'No se alcanza el servicio. En el servidor defina WHATSAPP_EMBEDDED=true o WHATSAPP_SERVICE_URL.'}
                </p>
              )}
              {status.reachable && status.connected && (
                <p style={{ color: '#2e7d32', margin: '6px 0' }}>
                  Conectado. Los envíos masivos usan pausas anti-bloqueo según el perfil elegido.
                  {typeof status.queueLength === 'number' ? ` Cola: ${status.queueLength}.` : ''}
                </p>
              )}
              {status.reachable && !status.connected && status.embedDiagnostics?.lastConnectError && (
                <p style={{ color: '#b71c1c', margin: '6px 0', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                  <strong>Error al iniciar Baileys:</strong> {status.embedDiagnostics.lastConnectError}
                </p>
              )}
              {status.reachable && !status.connected && (
                <>
                  <p style={{ margin: '6px 0' }}>
                    <strong>WhatsApp no está conectado.</strong> Escanee el QR con el teléfono (Dispositivos vinculados).
                  </p>
                  {status.qrBase64 ? (
                    <img src={status.qrBase64} alt="Código QR WhatsApp" style={{ maxWidth: 240, display: 'block' }} />
                  ) : (
                    <p style={{ color: '#666' }}>Generando QR… pulse «Actualizar estado / QR».</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
